import {useTranslation} from '@aboutyou/i18n';
import {
  ANCHOR_OFFSET_FROM_STICKY_HEADER,
  BasCategoryHeaderBanner,
  CategoryBreadcrumbs,
  CategoryHeadSeo,
  CategoryInlineFilter,
  CategoryNavigationTree,
  CategoryProductStream,
  CategoryStreamHeader,
  InteractiveProductTile,
  LoadMoreComponentDisplayConfig,
  ProductMiniAdpWithBasketFlyout,
  PromotionBanner,
  STREAM_TOP_SPACING,
  SizeSelectionOpenedStateKey,
  useCategoryLandingPageTest,
  useIsInteractiveTileActive,
  useIsNewBannerFrameworkActive,
  useMiniAdpDialogOpenedState,
  useProductStreamDisplayState,
} from '@aboutyou/logic-components';
import {ScrollTopAnchor} from '@aboutyou/router';
import {
  ModalDialog,
  SPACING,
  SplitView,
  Z_INDEXES,
} from '@aboutyou/ui-components';
import React, {FC, StrictMode, Suspense, useRef} from 'react';
import {Sticky} from 'react-stickup-ay';
import styled from 'styled-components';
import {useADPPreload} from '../ArticleDetailPage/useADPPreload';

const loadMoreComponentDisplayConfig: LoadMoreComponentDisplayConfig = {
  tilesBeforeFirstDisplay: 10000,
  tilesBeforeConsecutivelyDisplay: 10000,
};

export const Category: FC = () => {
  const {t} = useTranslation();
  const {productImageType, updateProductImageType} =
    useProductStreamDisplayState();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const inlineFiltersRef = useRef<HTMLDivElement | null>(null);

  const {open: openProductMiniADPDialog} = useMiniAdpDialogOpenedState();

  const interactiveCards = useIsInteractiveTileActive();

  const activateNewBannerFramework = useIsNewBannerFrameworkActive();

  useCategoryLandingPageTest();

  useADPPreload();

  return (
    <StrictMode>
      <CategoryHeadSeo />
      <ScrollTopAnchor
        defaultOffsetTop={ANCHOR_OFFSET_FROM_STICKY_HEADER}
        as={SplitView}
        split={'30:70'}
        splitMedium={'20:80'}
        splitLarge={'15:85'}
        refStart={sidebarRef}
        contentStart={
          <Suspense>
            <SidebarNavigationAside aria-label={t('Category navigation')}>
              <Sticky
                overflowScroll="flow"
                experimentalNative
                container={sidebarRef}
                style={{zIndex: Z_INDEXES.CATEGORY_TREE}}
                defaultOffsetTop={ANCHOR_OFFSET_FROM_STICKY_HEADER}
              >
                <StyledCategoryTree />
              </Sticky>
            </SidebarNavigationAside>
          </Suspense>
        }
        contentEnd={
          <Suspense>
            <ContentSection>
              {activateNewBannerFramework ? (
                <PromotionBannerWrapper>
                  <PromotionBanner
                    ContentPopperLayoutComponent={ModalDialog}
                    showSliderNavigation
                  />
                </PromotionBannerWrapper>
              ) : (
                <BasCategoryHeaderBanner />
              )}
              <CategoryBreadcrumbs />
              <CategoryStreamHeader
                productImageType={productImageType}
                onChangeProductImageType={updateProductImageType}
                inlineFiltersRef={inlineFiltersRef}
              />
              <FiltersAndProductStreamWrapper>
                <CategoryInlineFilter inlineFiltersRef={inlineFiltersRef} />
                <CategoryProductStream
                  productTileGalleryMode={
                    interactiveCards ? 'hover' : undefined
                  }
                  constrainStoryTilesToViewportBoundary
                  gridColumns={undefined}
                  preferredProductImageType={productImageType}
                  inlineBASPlacements
                  loadMoreComponentDisplayConfig={
                    loadMoreComponentDisplayConfig
                  }
                  onClickAddCta={openProductMiniADPDialog}
                  interactiveHoverMode={interactiveCards}
                  ProductTileComponent={InteractiveProductTile}
                />
              </FiltersAndProductStreamWrapper>
            </ContentSection>
          </Suspense>
        }
      />

      <ProductMiniAdpWithBasketFlyout
        showAddToWishlistButton
        sizeSelectionStateManagerKey={
          SizeSelectionOpenedStateKey.MAIN_SIZE_SELECTION
        }
      />
    </StrictMode>
  );
};

const ContentSection = styled.section`
  padding-top: ${STREAM_TOP_SPACING}px;
`;

// `<aside>` is needed for the landmark a11y rules, but it must not introduce
// a CSS box: Sticky's `container={sidebarRef}` positioning depends on being
// nested exactly as it was before this wrapper was added.
const SidebarNavigationAside = styled.aside`
  display: contents;
`;

const PromotionBannerWrapper = styled.div`
  margin-bottom: ${SPACING['Y.Xl2']};

  &:empty {
    margin-bottom: ${SPACING['Y.None']};
  }
`;

const StyledCategoryTree = styled(CategoryNavigationTree)`
  padding-top: ${STREAM_TOP_SPACING}px;
  word-break: break-word;
`;

const FiltersAndProductStreamWrapper = styled.div`
  overflow: hidden;
`;
